import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../hooks/useT';

type Props = {
  /** The consent text to show. Empty/null → the banner does not render. */
  text: string | null;
  /** Scopes the localStorage acknowledgement key. Null → 'platform'. */
  tenantKey: string | null;
};

function ackKey(tenantKey: string | null): string {
  return `cookie_ack_${tenantKey ?? 'platform'}`;
}

/** Builds the privacy-page target, preserving an existing ?tenant= query param if present. */
function privacyHref(): string {
  const params = new URLSearchParams(window.location.search);
  const tenant = params.get('tenant');
  return tenant ? `/datenschutz?tenant=${encodeURIComponent(tenant)}` : '/datenschutz';
}

/**
 * ISSUE-0082: single-notice cookie-consent banner. Fixed bottom bar styled with the tenant brand
 * color. Dismissal is remembered per tenant in localStorage; hidden once acknowledged or when there
 * is no text to show.
 */
export function CookieBanner({ text, tenantKey }: Props) {
  const { t } = useT();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(ackKey(tenantKey)) === '1';
    } catch {
      return false;
    }
  });

  if (!text || dismissed) return null;

  const accept = () => {
    try {
      localStorage.setItem(ackKey(tenantKey), '1');
    } catch {
      // localStorage unavailable (private mode / disabled) — still hide for this session.
    }
    setDismissed(true);
  };

  return (
    <div
      role="region"
      aria-label={t('cookie.aria_label')}
      className="fixed inset-x-0 bottom-0 z-50 border-t border-slate-200 bg-white/95 shadow-lg backdrop-blur"
    >
      <div className="mx-auto flex max-w-3xl flex-col gap-3 px-4 py-3 text-sm text-slate-700 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex-1">
          {text}{' '}
          <Link to={privacyHref()} className="text-brand underline underline-offset-2 hover:text-brand-hover">
            {t('cookie.privacy_link')}
          </Link>
        </p>
        <button
          type="button"
          onClick={accept}
          className="shrink-0 rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-ring"
        >
          {t('cookie.accept')}
        </button>
      </div>
    </div>
  );
}
